import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '../hooks/useAuth.jsx'
import { Button, Input, Card, Alert, useToast } from '../components'
import PhoneInput from '../shared/components/PhoneInput.jsx'
import AppHeader from '../shared/components/AppHeader.jsx'
import LanguagePreferenceCard from '../shared/i18n/LanguagePreferenceCard.jsx'
import apiClient from '../api/index.js'
import { asMessage } from '../shared/utils/errorDetail'

// Changing your own email needs your current password (identity PUT /user/me): the email is where a
// password reset goes, so changing it is changing who can take the account. The box shows only when
// the email has actually changed; the other fields never ask for it.
const sameEmail = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase()

const Profile = () => {
  const { user, updateProfile, changePassword, loading, error, clearError } = useAuth()
  const { showSuccess, showError } = useToast()
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  })
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  })
  const [errors, setErrors] = useState({})
  // Only while the email field holds a different address. Never kept after a save or a cancel.
  const [emailPassword, setEmailPassword] = useState('')
  const [passwordErrors, setPasswordErrors] = useState({})
  const [success, setSuccess] = useState(false)
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordError, setPasswordError] = useState(null)
  const [showPasswordSuccessBanner, setShowPasswordSuccessBanner] = useState(false)
  const passwordSuccessRef = useRef(null)

  // Notification preferences
  const [notifyDailyReports, setNotifyDailyReports] = useState(true)
  const [notifLoading, setNotifLoading] = useState(false)

  // Initialize form data when user data is available
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || ''
      })
    }
  }, [user])

  // Load notification preferences
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const res = await apiClient.get('/user/me/notifications')
        if (!cancelled) setNotifyDailyReports(res.data.notify_daily_reports)
      } catch { /* ignore */ }
    }
    if (user) load()
    return () => { cancelled = true }
  }, [user])

  // Clear errors and success messages after a delay
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(false), 3000)
      return () => clearTimeout(timer)
    }
  }, [success])

  // Effect to handle password success banner
  useEffect(() => {
    if (showPasswordSuccessBanner) {
      // Scroll to the success message if it exists
      if (passwordSuccessRef.current) {
        passwordSuccessRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      // Clear success banner after 10 seconds
      const timer = setTimeout(() => {
        setShowPasswordSuccessBanner(false);
        setPasswordSuccess(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [showPasswordSuccessBanner]);

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear field error when user starts typing
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required'
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!sameEmail(formData.email, user?.email) && !emailPassword) {
      newErrors.current_password_for_email = 'Enter your current password to change your email.'
    }

    // eslint-disable-next-line no-useless-escape
    if (formData.phone && !/^\+?[\d\s\-\(\)]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validatePasswordForm = () => {
    const newErrors = {}

    if (!passwordData.current_password.trim()) {
      newErrors.current_password = 'Current password is required'
    }

    if (!passwordData.new_password.trim()) {
      newErrors.new_password = 'New password is required'
    } else if (passwordData.new_password.length < 8) {
      newErrors.new_password = 'Password must be at least 8 characters long'
    }

    if (!passwordData.confirm_password.trim()) {
      newErrors.confirm_password = 'Please confirm your new password'
    } else if (passwordData.new_password !== passwordData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match'
    }

    setPasswordErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) return

    const emailChanged = !sameEmail(formData.email, user?.email)
    const result = await updateProfile(emailChanged ? { ...formData, current_password: emailPassword } : formData)
    setEmailPassword('')
    
    if (result.success) {
      showSuccess('Profile updated successfully!')
      setSuccess(true)
      setIsEditing(false)
      clearError()
    } else {
      showError(asMessage(result.error) || 'Failed to update profile. Please try again.')
    }
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    
    if (!validatePasswordForm()) return

    setPasswordLoading(true)
    setPasswordError(null)
    
    console.log('Submitting password change...')

    try {
      // Call the changePassword function from useAuth hook
      const result = await changePassword(
        passwordData.current_password, 
        passwordData.new_password
      )
      
      console.log('Password change result:', result)
      
      if (result && result.success) {
        console.log('Password changed successfully! Showing toast notification...');
        
        // Show success toast notification
        showSuccess('Password changed successfully! Your new password will be required for next login.');
        
        // Reset form
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        
        // Close the change password form
        setIsChangingPassword(false);
        
        // Clear any existing password errors
        setPasswordError(null);
        
        console.log('Toast notification should now be visible');
      } else {
        console.log('Password change failed:', result?.error || 'Unknown error')
        const errorMessage = result?.error || 'Password change failed'
        setPasswordError(errorMessage)
        showError(errorMessage)
      }
    } catch (error) {
      console.error('Password change exception:', error)
      const errorMessage = 'An unexpected error occurred while changing password'
      setPasswordError(errorMessage)
      showError(errorMessage)
    } finally {
      setPasswordLoading(false)
    }
  }

  const handleNotifToggle = useCallback(async (value) => {
    setNotifLoading(true)
    try {
      const res = await apiClient.put('/user/me/notifications', { notify_daily_reports: value })
      setNotifyDailyReports(res.data.notify_daily_reports)
      showSuccess(value ? 'Daily report emails enabled' : 'Daily report emails disabled')
    } catch {
      showError('Failed to update notification preferences')
    } finally {
      setNotifLoading(false)
    }
  }, [showSuccess, showError])

  const handleCancel = () => {
    // Reset form to original user data
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || ''
      })
    }
    setErrors({})
    setEmailPassword('')
    setIsEditing(false)
    clearError()
  }

  const handlePasswordCancel = () => {
    // Reset password form
    setPasswordData({
      current_password: '',
      new_password: '',
      confirm_password: ''
    })
    setPasswordErrors({})
    setIsChangingPassword(false)
    setPasswordError(null)
  }

  if (!user) {
    return <div>Loading...</div>
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ display: 'flex', flexDirection: 'column' }}>
      <AppHeader 
        title="My Profile"
        showProfileButton={false}
        user={user}
      />
      
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8" style={{ flex: 1 }}>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Information */}
          <div className="lg:col-span-2">
            <Card 
              title="Personal Information"
              headerActions={
                !isEditing && (
                  <Button
                    variant="secondary"
                    onClick={() => setIsEditing(true)}
                    className="bfs-button-secondary"
                  >
                    Edit Profile
                  </Button>
                )
              }
            >
              {success && (
                <Alert
                  type="success"
                  title="Profile Updated"
                  message="Your profile has been successfully updated."
                  onClose={() => setSuccess(false)}
                  className="mb-6"
                />
              )}

              {error && (
                <Alert
                  type="error"
                  title="Update Failed"
                  message={asMessage(error)}
                  onClose={clearError}
                  className="mb-6"
                />
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Input
                    label="First Name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    error={errors.first_name}
                    required
                    disabled={!isEditing}
                  />

                  <Input
                    label="Last Name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    error={errors.last_name}
                    required
                    disabled={!isEditing}
                  />
                </div>

                <Input
                  label="Email Address"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={errors.email}
                  required
                  disabled={!isEditing}
                />

                {isEditing && !sameEmail(formData.email, user.email) && (
                  <Input
                    label="Current password (needed to change your email)"
                    type="password"
                    name="current_password_for_email"
                    value={emailPassword}
                    onChange={(e) => {
                      setEmailPassword(e.target.value)
                      if (errors.current_password_for_email) setErrors((prev) => ({ ...prev, current_password_for_email: '' }))
                    }}
                    error={errors.current_password_for_email}
                    autoComplete="current-password"
                    helperText="Your email is where a password reset is sent, so changing it needs your password."
                  />
                )}

                <PhoneInput
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  error={errors.phone}
                  placeholder="(555) 123-4567"
                />

                {isEditing && (
                  <div className="flex justify-end space-x-3">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleCancel}
                      disabled={loading}
                      className="bfs-button-secondary"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      loading={loading}
                      disabled={loading}
                      className="bfs-button-primary"
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                )}
              </form>
            </Card>

            {/* Password Change Section */}
            <div className="mt-8">
              <Card 
                title="Security"
                headerActions={
                  !isChangingPassword && (
                    <Button
                      variant="secondary"
                      onClick={() => setIsChangingPassword(true)}
                      className="bfs-button-secondary"
                    >
                      Change Password
                    </Button>
                  )
                }
              >
                {passwordSuccess && (
                  <Alert
                    type="success"
                    title="Password Updated"
                    message="Your password has been successfully changed."
                    onClose={() => setPasswordSuccess(false)}
                    className="mb-6"
                  />
                )}

                {passwordError && (
                  <Alert
                    type="error"
                    title="Password Change Failed"
                    message={passwordError}
                    onClose={() => setPasswordError(null)}
                    className="mb-6"
                  />
                )}

                {!isChangingPassword ? (
                  <p className="text-gray-600">
                    Change your password to maintain account security. Regular password updates are recommended.
                  </p>
                ) : (
                  <form onSubmit={handlePasswordSubmit} className="space-y-6">
                    <Input
                      label="Current Password"
                      type="password"
                      name="current_password"
                      value={passwordData.current_password}
                      onChange={handlePasswordInputChange}
                      error={passwordErrors.current_password}
                      required
                    />

                    <Input
                      label="New Password"
                      type="password"
                      name="new_password"
                      value={passwordData.new_password}
                      onChange={handlePasswordInputChange}
                      error={passwordErrors.new_password}
                      required
                      helperText="Password must be at least 8 characters long"
                    />

                    <Input
                      label="Confirm New Password"
                      type="password"
                      name="confirm_password"
                      value={passwordData.confirm_password}
                      onChange={handlePasswordInputChange}
                      error={passwordErrors.confirm_password}
                      required
                    />

                    <div className="flex justify-end space-x-3">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handlePasswordCancel}
                        disabled={passwordLoading}
                        className="bfs-button-secondary"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        loading={passwordLoading}
                        disabled={passwordLoading}
                        className="bfs-button-primary"
                      >
                        {passwordLoading ? 'Saving...' : 'Update Password'}
                      </Button>
                    </div>
                  </form>
                )}
              </Card>
            </div>

            {/* LANGUAGE — deliberately ABOVE the notification settings. Somebody who has landed in a
                language they cannot read came here to fix exactly that, and making them scroll past
                an email-preferences block written in that language to do it is the same trap the
                sign-in page had. The card renders in the language being chosen. */}
            <div className="mt-8">
              <Card>
                <LanguagePreferenceCard />
              </Card>
            </div>

            {/* Notification Preferences */}
            <div className="mt-8">
              <Card title="Email Notifications">
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Choose which email notifications you'd like to receive. In-app notifications are always active.
                  </p>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Daily Reports</p>
                      <p className="text-xs text-gray-500">Notifications when reports are submitted, approved, or rejected</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={notifyDailyReports}
                      disabled={notifLoading}
                      onClick={() => handleNotifToggle(!notifyDailyReports)}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-maroon-600 focus:ring-offset-2 ${notifyDailyReports ? 'bg-bfs-maroon' : 'bg-gray-200'} ${notifLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${notifyDailyReports ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Account Information */}
          <div>
            <Card title="Account Information">
              <div className="space-y-4">
                {/* User ID removed - only needed for backend/API, not for human users */}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Role
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                      user.role === 'master_admin' ? 'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-600/20' :
                      user.role === 'admin' ? 'bg-red-50 text-red-700 ring-red-600/10' :
                      'bg-gray-50 text-gray-600 ring-gray-500/10'
                    }`}>
                      {user.role}
                    </span>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    <span className={user.is_active ? 'bfs-badge-active' : 'bfs-badge-inactive'}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                </div>

                {user.created_at && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Member Since
                    </label>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(user.created_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Profile
